import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription, interval } from 'rxjs';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { MachineTypeDTO } from 'src/app/modules/shared/interface/machine-type.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';
import { MachineTypeService } from 'src/app/modules/shared/services/machine-type.service';
import { MachineService } from 'src/app/modules/shared/services/machine.service';

import { ShiftRosterService } from 'src/app/modules/job-operation/services/shift-roster.service';
import { OperatorService } from 'src/app/modules/shared/services/operator.service';

import { AuthService } from 'src/app/core';
import { untracked } from '@angular/core';

@Component({
  selector: 'app-machine-comparison',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './machine-comparison.component.html',
  styleUrl: './machine-comparison.component.scss'
})
export class MachineComparisonComponent implements OnInit, OnDestroy {

  private router = inject(Router);
  private machineTypeService = inject(MachineTypeService);
  private departmentService = inject(DepartmentService);
  private machineService = inject(MachineService);

  private shiftRosterService = inject(ShiftRosterService);
  private operatorService = inject(OperatorService);

  private authService = inject(AuthService);

  // ── Signals ───────────────────────────────────────────────────────────────
  protected machineTypes = signal<MachineTypeDTO[]>([]);
  protected $$departmentDTO = signal<Department[]>([]);
  protected departmentId = signal<number | null>(null);
  activeTab = signal<MachineTypeDTO | null>(null);
  protected selectedMachineId = signal<number | null>(null);

  /** True when the active tab is the Die-cut section */
  protected isDiecut = computed(() => {
    const tabName = this.activeTab()?.machineTypeName?.toLowerCase() ?? '';
    // Matches both "Die Cutting" (Offset) and "Flexo Die Cutting" (Flexo)
    const normalized = tabName.replace(/\s+/g, '');
    if (!normalized.includes('diecutting')) {
      return false;
    }

    const hasAll = this.authService.hasContextPermission('machine-comparison', 'all');
    const hasDiecut1 = this.authService.hasContextPermission('machine-comparison', 'diecut1');
    const hasDiecut2 = this.authService.hasContextPermission('machine-comparison', 'diecut2');

    // If context is 'all' or no specific diecut context is present, show single display (return false)
    if (hasAll || (!hasDiecut1 && !hasDiecut2)) {
      return false;
    }

    return true;
  });

  protected selectedMachineName = computed(() => {
    const id = this.selectedMachineId();
    if (!id) return 'UNKNOWN MACHINE';
    const machine = this.filteredMachines().find(m => m.machineId === id);
    return machine ? machine.machineName : 'UNKNOWN MACHINE';
  });

  // ── Second machine (Die-cut panel 2) ─────────────────────────────────────
  protected selectedMachineId2 = signal<number | null>(null);

  protected selectedMachineName2 = computed(() => {
    const id = this.selectedMachineId2();
    if (!id) return 'UNKNOWN MACHINE';
    const machine = this.filteredMachines().find(m => m.machineId === id);
    return machine ? machine.machineName : 'UNKNOWN MACHINE';
  });

  protected selectedDate = signal<string>(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`
  );
  protected isDark = signal<boolean>(false);
  protected isTrackingLiveDate = false;
  protected liveShiftDate = signal<string | null>(null);

  onDateChange(newDate: string): void {
    if (this.liveShiftDate() && newDate === this.liveShiftDate()) {
      this.isTrackingLiveDate = true;
    } else {
      this.isTrackingLiveDate = false;
    }
    this.selectedDate.set(newDate);
  }

  protected selectedMonthName = computed(() => {
    const dateStr = this.selectedDate();
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleString('default', { month: 'long' });
  });

  // ── Auto Refresh ──────────────────────────────────────────────────────────
  protected refreshTrigger = signal<number>(0);
  protected lastUpdatedTime = signal<string>('');
  private refreshSub?: Subscription;

  // ══════════════════════════════════════════════════════════════════════════
  // ── MACHINE 1 Dashboard Stats ─────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════
  protected dailyPlanned = signal<number>(0);
  protected dailyOutput = signal<number>(0);
  protected outputAveragePerHour = signal<number>(0);
  protected makeReadyCount = signal<number>(0);
  protected shiftDetails = signal<any[]>([]);
  protected unitOfMachine = signal<string>('impressions');

  protected dayShiftOperators = signal<string[]>([]);
  protected nightShiftOperators = signal<string[]>([]);
  protected showAllDayOps = signal(false);
  protected showAllNightOps = signal(false);
  protected activeShiftName = signal<string | null>(null);
  protected realtimeShiftName = signal<string | null>(null);
  protected machineStatus = signal<string>('unknown');

  protected todayRosterData = signal<any[]>([]);

  // ── Machine 1 Timeline Signals ────────────────────────────────────────────
  protected timelineSegments = signal<any[]>([]);
  protected timelineAxis = signal<string[]>([]);
  protected timelineTotalDurationStr = signal<string>('0h 0m');
  protected timelineWindow = signal<{ start: string, end: string } | null>(null);

  // ── Make Ready Timer Signals & State (Machine 1) ──────────────────────────
  protected makeReadyTimerStr = signal<string>('00:00:00');
  protected makeReadyTimerStatus = signal<'running' | 'paused' | 'reset' | 'idle'>('idle');
  private makeReadyBaseMs1 = 0;
  private makeReadyRunningStartTs1?: number;

  // ── Operational Timer Signals & State (Machine 1) ─────────────────────────
  protected operationalTimerStr = signal<string>('00:00:00');
  protected operationalTimerStatus = signal<'running' | 'paused' | 'reset' | 'idle'>('idle');
  private operationalBaseMs1 = 0;
  private operationalRunningStartTs1?: number;

  // ── Machine 1 Monthly Stats ────────────────────────────────────────────────
  protected monthlyTotalAchieved = signal<number>(0);
  protected monthlyTotalMakeReady = signal<number>(0);
  protected monthlyTotalPlanned = signal<number>(0);
  protected monthlyTotalWorkedHours = signal<number>(0);

  // ── Machine 1 Operator Performance ────────────────────────────────────────
  protected operatorPerformances = signal<any[]>([]);

  // ══════════════════════════════════════════════════════════════════════════
  // ── MACHINE 2 Dashboard Stats (Die-cut panel 2) ───────────────────────────
  // ══════════════════════════════════════════════════════════════════════════
  protected dailyPlanned2 = signal<number>(0);
  protected dailyOutput2 = signal<number>(0);
  protected outputAveragePerHour2 = signal<number>(0);
  protected makeReadyCount2 = signal<number>(0);
  protected shiftDetails2 = signal<any[]>([]);
  protected unitOfMachine2 = signal<string>('impressions');

  protected dayShiftOperators2 = signal<string[]>([]);
  protected nightShiftOperators2 = signal<string[]>([]);
  protected showAllDayOps2 = signal(false);
  protected showAllNightOps2 = signal(false);
  protected activeShiftName2 = signal<string | null>(null);
  protected realtimeShiftName2 = signal<string | null>(null);
  protected machineStatus2 = signal<string>('unknown');

  // ── Machine 2 Timeline Signals ────────────────────────────────────────────
  protected timelineSegments2 = signal<any[]>([]);
  protected timelineAxis2 = signal<string[]>([]);
  protected timelineTotalDurationStr2 = signal<string>('0h 0m');
  protected timelineWindow2 = signal<{ start: string, end: string } | null>(null);

  // ── Make Ready Timer Signals & State (Machine 2) ──────────────────────────
  protected makeReadyTimerStr2 = signal<string>('00:00:00');
  protected makeReadyTimerStatus2 = signal<'running' | 'paused' | 'reset' | 'idle'>('idle');
  private makeReadyBaseMs2 = 0;
  private makeReadyRunningStartTs2?: number;

  // ── Operational Timer Signals & State (Machine 2) ─────────────────────────
  protected operationalTimerStr2 = signal<string>('00:00:00');
  protected operationalTimerStatus2 = signal<'running' | 'paused' | 'reset' | 'idle'>('idle');
  private operationalBaseMs2 = 0;
  private operationalRunningStartTs2?: number;

  // ── Machine 2 Monthly Stats ────────────────────────────────────────────────
  protected monthlyTotalAchieved2 = signal<number>(0);
  protected monthlyTotalMakeReady2 = signal<number>(0);
  protected monthlyTotalPlanned2 = signal<number>(0);
  protected monthlyTotalWorkedHours2 = signal<number>(0);

  // ── Machine 2 Operator Performance ────────────────────────────────────────
  protected operatorPerformances2 = signal<any[]>([]);

  // ── Shared Data Signals ───────────────────────────────────────────────────
  protected sharedCurrentShifts = signal<any[]>([]);
  protected operatorNcrCounts = signal<Record<string, number>>({});
  protected operatorNcrData = signal<Record<string, any[]>>({});
  protected operatorNcrTotals = signal<Record<string, number>>({});

  // ── NCR Modal Signals ─────────────────────────────────────────────────────
  protected ncrModalOpen = signal<boolean>(false);
  protected selectedNcrOperatorName = signal<string>('');
  protected operatorNcrDetails = signal<any[]>([]);
  protected totalNcrValue = computed(() => this.operatorNcrDetails().reduce((sum, ncr) => sum + (Number(ncr.ncrValue) || 0), 0));
  // ── Live shift date for machine 2 ─────────────────────────────────────────
  protected liveShiftDate2 = signal<string | null>(null);

  // ══════════════════════════════════════════════════════════════════════════
  // ── COMPUTED – Machine 1 ──────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  protected getFirstName(fullName: string | null | undefined): string | null {
    if (!fullName) return null;
    const parts = fullName.trim().split(' ');
    const namePart = parts.find(part => part.length > 1 && !part.endsWith('.'));
    return namePart || parts[0];
  }

  protected getFirstNamesArr(fullNames: string[] | null | undefined): string[] {
    if (!fullNames || fullNames.length === 0) return [];
    return fullNames.map(name => {
      const parts = name.trim().split(' ');
      const namePart = parts.find(part => part.length > 1 && !part.endsWith('.'));
      return namePart || parts[0];
    });
  }

  protected getBestOperatorLines(fullName: string | null | undefined): string[] {
    if (!fullName) return ['â€”'];
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return [parts[0]];
    return [parts[0], parts[1]];
  }

  protected monthlyCurrentOperatorNames = computed(() => {
    const liveShift = this.realtimeShiftName()?.toLowerCase().trim();
    const machineId = this.selectedMachineId();
    const todayData = this.todayRosterData();

    if (!liveShift || !machineId || !todayData) return [];

    const rm = todayData.find((m: any) => m.machineId === machineId);
    if (!rm || !rm.shiftRoster) return [];

    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayEntries = rm.shiftRoster.filter((r: any) => {
      const rDate = r.date ? r.date.substring(0, 10) : '';
      return rDate === todayStr && r.rosterStatus?.toUpperCase() === 'ACTIVE';
    });

    const entries = todayEntries.filter((r: any) => {
      const rosterShift = r.shift?.toLowerCase().trim() || '';
      return rosterShift === liveShift || rosterShift.includes(liveShift) || liveShift.includes(rosterShift);
    });

    return entries.map((e: any) => e.operator).filter(Boolean);
  });

  // Machine 2 current operator names
  protected monthlyCurrentOperatorNames2 = computed(() => {
    const liveShift = this.realtimeShiftName2()?.toLowerCase().trim();
    const machineId = this.selectedMachineId2();
    const todayData = this.todayRosterData();

    if (!liveShift || !machineId || !todayData) return [];

    const rm = todayData.find((m: any) => m.machineId === machineId);
    if (!rm || !rm.shiftRoster) return [];

    const d = new Date();
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayEntries = rm.shiftRoster.filter((r: any) => {
      const rDate = r.date ? r.date.substring(0, 10) : '';
      return rDate === todayStr && r.rosterStatus?.toUpperCase() === 'ACTIVE';
    });

    const entries = todayEntries.filter((r: any) => {
      const rosterShift = r.shift?.toLowerCase().trim() || '';
      return rosterShift === liveShift || rosterShift.includes(liveShift) || liveShift.includes(rosterShift);
    });

    return entries.map((e: any) => e.operator).filter(Boolean);
  });

  protected bestOperator = computed(() => {
    const ops = this.operatorPerformances();
    if (!ops || ops.length === 0) return null;
    return ops[0];
  });

  protected topRunnersUp = computed(() => {
    const ops = this.operatorPerformances();
    if (!ops || ops.length <= 1) return [];
    return ops.slice(1, 4);
  });

  protected currentOperatorPerfs = computed(() => {
    const ops = this.operatorPerformances();
    const activeOpNames = this.monthlyCurrentOperatorNames();

    if (!activeOpNames || activeOpNames.length === 0) return [];

    return activeOpNames.map((activeOpName: string) => {
      const activeNameClean = activeOpName.toLowerCase().trim();
      const index = ops ? ops.findIndex((o: any) => {
        const oName = o.operatorName?.toLowerCase().trim() || '';
        return oName === activeNameClean || oName.includes(activeNameClean) || activeNameClean.includes(oName);
      }) : -1;

      if (index === -1) {
        return {
          operatorName: activeOpName,
          machineTotalOutput: 0,
          makeReadyCount: 0,
          rank: '-',
          gapToTop: '-'
        };
      }

      return {
        ...ops[index],
        rank: index + 1,
        gapToTop: index === 0 ? 0 : ops[0].machineTotalOutput - ops[index].machineTotalOutput
      };
    });
  });

  protected monthlyAchievementPct = computed(() => {
    const planned = this.monthlyTotalPlanned();
    const achieved = this.monthlyTotalAchieved();
    if (!planned || planned <= 0) return 0;
    return Math.round((achieved / planned) * 100);
  });

  protected monthDivisor = computed(() => {
    const dateStr = this.selectedDate();
    if (!dateStr) return 1;

    const parts = dateStr.split('-');
    const selYear = parseInt(parts[0], 10);
    const selMonth = parseInt(parts[1], 10);

    const today = new Date();
    const currYear = today.getFullYear();
    const currMonth = today.getMonth() + 1;

    let divisor = 1;
    if (selYear < currYear || (selYear === currYear && selMonth < currMonth)) {
      divisor = new Date(selYear, selMonth, 0).getDate();
    } else {
      divisor = today.getDate();
    }
    return divisor > 0 ? divisor : 1;
  });

  protected avgHourlyOutput = computed(() => {
    const achieved = this.monthlyTotalAchieved();
    const hours = this.monthlyTotalWorkedHours();
    if (achieved <= 0 || hours <= 0) return 0;
    return Math.round(achieved / hours);
  });

  protected bestOperatorAvgDailyOutput = computed(() => {
    const bestOp = this.bestOperator();
    if (!bestOp) return 0;
    const achieved = bestOp.machineTotalOutput || 0;
    if (achieved <= 0) return 0;
    return Math.round(achieved / this.monthDivisor());
  });

  protected bestOperatorAvgHourlyOutput = computed(() => {
    const bestOp = this.bestOperator();
    if (!bestOp) return 0;
    const hours = Number(bestOp.totalHoursWorked) || 0;
    const achieved = bestOp.machineTotalOutput || 0;
    if (hours <= 0 || achieved <= 0) return 0;
    return Math.round(achieved / hours);
  });



  protected dayShift = computed(() => this.shiftDetails().find(s => s.shiftName?.trim().toLowerCase() === 'day shift') || null);
  protected nightShift = computed(() => this.shiftDetails().find(s => s.shiftName?.trim().toLowerCase() === 'night shift') || null);

  protected formatHoursToHHMM(hours: number | null | undefined): string {
    if (hours == null || isNaN(hours) || hours <= 0) return '0m';
    const totalMins = Math.round(hours * 60);
    if (totalMins <= 0) return '0m';
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  protected getShiftMakeReadyTimeStr(shift: any): string {
    if (!shift) return '0m';
    const hours = Number(shift.makeReadyTime) || 0;
    return this.formatHoursToHHMM(hours);
  }

  protected getShiftAvgMakeReadyTimeStr(shift: any): string {
    if (!shift) return '0m';
    const count = Number(shift.makeReadyCount) || 0;
    const hours = Number(shift.makeReadyTime) || 0;
    if (count <= 0 || hours <= 0) return '0m';
    const avgHours = hours / count;
    return this.formatHoursToHHMM(avgHours);
  }

  protected goalAchievementPct = computed(() => {
    const planned = this.dailyPlanned();
    const output = this.dailyOutput();
    if (!planned || planned <= 0) return 0;
    return Math.round((output / planned) * 100);
  });

  protected donutDashOffset = computed(() => {
    const circumference = 201.06;
    const pct = Math.min(100, this.goalAchievementPct());
    return circumference - (circumference * pct) / 100;
  });

  // ══════════════════════════════════════════════════════════════════════════
  // ── COMPUTED – Machine 2 ──────────────────────────────────────────────────
  // ══════════════════════════════════════════════════════════════════════════

  protected bestOperator2 = computed(() => {
    const ops = this.operatorPerformances2();
    if (!ops || ops.length === 0) return null;
    return ops[0];
  });

  protected topRunnersUp2 = computed(() => {
    const ops = this.operatorPerformances2();
    if (!ops || ops.length <= 1) return [];
    return ops.slice(1, 4);
  });

  protected currentOperatorPerfs2 = computed(() => {
    const ops = this.operatorPerformances2();
    const activeOpNames = this.monthlyCurrentOperatorNames2();

    if (!activeOpNames || activeOpNames.length === 0) return [];

    return activeOpNames.map((activeOpName: string) => {
      const activeNameClean = activeOpName.toLowerCase().trim();
      const index = ops ? ops.findIndex((o: any) => {
        const oName = o.operatorName?.toLowerCase().trim() || '';
        return oName === activeNameClean || oName.includes(activeNameClean) || activeNameClean.includes(oName);
      }) : -1;

      if (index === -1) {
        return {
          operatorName: activeOpName,
          machineTotalOutput: 0,
          makeReadyCount: 0,
          rank: '-',
          gapToTop: '-'
        };
      }
      return {
        ...ops[index],
        rank: index + 1,
        gapToTop: index === 0 ? 0 : ops[0].machineTotalOutput - ops[index].machineTotalOutput
      };
    });
  });

  protected monthlyAchievementPct2 = computed(() => {
    const planned = this.monthlyTotalPlanned2();
    const achieved = this.monthlyTotalAchieved2();
    if (!planned || planned <= 0) return 0;
    return Math.round((achieved / planned) * 100);
  });

  protected avgHourlyOutput2 = computed(() => {
    const achieved = this.monthlyTotalAchieved2();
    const hours = this.monthlyTotalWorkedHours2();
    if (achieved <= 0 || hours <= 0) return 0;
    return Math.round(achieved / hours);
  });

  protected bestOperatorAvgHourlyOutput2 = computed(() => {
    const bestOp = this.bestOperator2();
    if (!bestOp) return 0;
    const hours = Number(bestOp.totalHoursWorked) || 0;
    const achieved = bestOp.machineTotalOutput || 0;
    if (hours <= 0 || achieved <= 0) return 0;
    return Math.round(achieved / hours);
  });



  protected dayShift2 = computed(() => this.shiftDetails2().find(s => s.shiftName?.trim().toLowerCase() === 'day shift') || null);
  protected nightShift2 = computed(() => this.shiftDetails2().find(s => s.shiftName?.trim().toLowerCase() === 'night shift') || null);

  protected goalAchievementPct2 = computed(() => {
    const planned = this.dailyPlanned2();
    const output = this.dailyOutput2();
    if (!planned || planned <= 0) return 0;
    return Math.round((output / planned) * 100);
  });

  protected donutDashOffset2 = computed(() => {
    const circumference = 201.06;
    const pct = Math.min(100, this.goalAchievementPct2());
    return circumference - (circumference * pct) / 100;
  });

  // ── Date / clock ──────────────────────────────────────────────────────────
  today: string = '';
  currentTime: string = '';
  private clockSub?: Subscription;

  // ── Auth-context constraints ───────────────────────────────────────────────
  protected allowedContext = computed(() => {
    if (this.authService.hasContextPermission('machine-comparison', 'all')) return null;

    let context: string[] = [];
    this.authService.getAuthState().subscribe(state => {
      const perm = state.permissions.find(p => p.resourcePath === 'machine-comparison');
      if (perm) context = perm.context as unknown as string[];
    }).unsubscribe();

    if (context.length === 0) return null;
    const parts = context.map((v: string) => Number(v)).filter((n: number) => !isNaN(n) && n > 0);
    if (parts.length === 0) return null;
    return { departmentId: parts[0], machineTypeIds: parts.slice(1) };
  });

  protected visibleDepartments = computed(() => {
    const ctx = this.allowedContext();
    const all = this.$$departmentDTO();
    if (!ctx) return all;
    return all.filter((d: Department) => d.departmentId === ctx.departmentId);
  });

  protected filteredMachineTypes = computed(() => {
    const deptId = this.departmentId();
    if (!deptId) return [];
    const ctx = this.allowedContext();
    const byDept = this.machineTypes().filter((mt: MachineTypeDTO) => mt.departmentId === deptId);
    if (!ctx) return byDept;
    return byDept.filter((mt: MachineTypeDTO) => ctx.machineTypeIds.includes(mt.id));
  });

  /** Machines in the active department that belong to the active machine-type tab */
  protected filteredMachines = computed(() => {
    const deptId = this.departmentId();
    const tabName = this.activeTab()?.machineTypeName?.toLowerCase();
    if (!deptId || !tabName) return [];

    const dept = this.$$departmentDTO().find(d => d.departmentId === deptId);
    if (!dept) return [];

    let machines = dept.machines.filter(
      (m: Machine) => m.machineTypeName?.toLowerCase() === tabName
    );

    // Matches both "Die Cutting" (Offset) and "Flexo Die Cutting" (Flexo)
    const normalized = tabName.replace(/\s+/g, '');
    const isDiecut = normalized.includes('diecutting');
    if (isDiecut) {
      const hasDiecut1 = this.authService.hasContextPermission('machine-comparison', 'diecut1');
      const hasDiecut2 = this.authService.hasContextPermission('machine-comparison', 'diecut2');

      if (hasDiecut1) {
        machines = machines.slice(0, 2);
      } else if (hasDiecut2) {
        machines = machines.slice(-2);
      }
    }

    return machines;
  });

  constructor() {
    this.fetchMachineTypes();
    this.fetchDepartments();

    // Auto-set department from auth context or first available
    effect(() => {
      const ctx = this.allowedContext();
      const types = this.machineTypes();
      const depts = this.$$departmentDTO();
      if (types.length === 0 || depts.length === 0) return;
      if (ctx) {
        this.departmentId.set(ctx.departmentId);
      } else if (!this.departmentId()) {
        this.departmentId.set(depts[0]?.departmentId ?? null);
      }
    });

    // Auto-select first machine-type tab when department changes
    effect(() => {
      const deptId = this.departmentId();
      const types = this.machineTypes();
      if (!deptId || types.length === 0) { this.activeTab.set(null); return; }
      const filtered = this.filteredMachineTypes();
      if (filtered.length === 0) { this.activeTab.set(null); return; }

      const hasDiecut1 = this.authService.hasContextPermission('machine-comparison', 'diecut1');
      const hasDiecut2 = this.authService.hasContextPermission('machine-comparison', 'diecut2');

      if (hasDiecut1 || hasDiecut2) {
        const diecutTab = filtered.find(tab => {
          const name = tab.machineTypeName?.toLowerCase() ?? '';
          return name.replace(/\s+/g, '').includes('diecutting');
        });
        if (diecutTab) {
          this.activeTab.set(diecutTab);
          return;
        }
      }

      this.activeTab.set(filtered[0]);
    });

    // Auto-select first machine whenever the tab or department changes
    effect(() => {
      const machines = this.filteredMachines();
      this.selectedMachineId.set(machines.length > 0 ? machines[0].machineId : null);
      this.selectedMachineId2.set(null);
    }, { allowSignalWrites: true });

    // Fetch Today's Roster to permanently lock the Current Operator identity
    effect(() => {
      this.refreshTrigger();
      const deptId = this.departmentId();
      if (!deptId) return;
      const today = new Date();
      this.todayRosterSub?.unsubscribe();
      this.todayRosterSub = this.shiftRosterService.getShiftRosterByDep({
        department: deptId,
        year: today.getFullYear(),
        month: today.getMonth() + 1
      }).subscribe((res: any) => {
        this.todayRosterData.set(res?.data?.machines || []);
      });
    }, { allowSignalWrites: true });

    // Fetch Daily Chart Data / Timeline – Machine 1
    effect(() => {
      this.refreshTrigger();
      const machineId = this.selectedMachineId();
      const date = this.selectedDate();

      this.chartDataSub?.unsubscribe();

      if (machineId && date) {
        const [year, month, day] = date.split('-');
        this.chartDataSub = this.machineService.getDailyChartData(machineId, { year: Number(year), month: Number(month), day: Number(day) }).subscribe((res: any) => {
          this.buildTimeline(res?.data, date);
        });
      }
    });

    // Fetch Daily Chart Data / Timeline – Machine 2
    effect(() => {
      this.refreshTrigger();
      const machineId = this.selectedMachineId2();
      const date = this.selectedDate();

      this.chartDataSub2?.unsubscribe();

      if (machineId && date) {
        const [year, month, day] = date.split('-');
        this.chartDataSub2 = this.machineService.getDailyChartData(machineId, { year: Number(year), month: Number(month), day: Number(day) }).subscribe((res: any) => {
          this.buildTimeline2(res?.data, date);
        });
      }
    });

    // Fetch Daily Impressions when machine or date changes – Machine 1
    effect(() => {
      this.refreshTrigger();
      const machineId = this.selectedMachineId();
      const date = this.selectedDate();

      this.impressionsSub?.unsubscribe();
      this.monthlySub?.unsubscribe();

      if (machineId && date) {
        const [year, month, day] = date.split('-');
        this.impressionsSub = this.machineService.getDailyReport(machineId, { year, month, day }).subscribe((res: any) => {
          if (res && res.data) {
            this.dailyPlanned.set(Number(res.data.plannedImpressions) || 0);
            this.dailyOutput.set(Number(res.data.actualImpressions) || 0);
            this.makeReadyCount.set(Number(res.data.makeReadyCount) || 0);

            const shifts = res.data.shiftWiseDetails || [];
            this.shiftDetails.set(shifts);
            this.unitOfMachine.set(res.data.unitOfMachine || 'impressions');
            if (res.data.currentShiftDate === date) {
              this.activeShiftName.set(res.data.currentShift?.trim() || null);
            } else {
              this.activeShiftName.set(null);
            }

            const dayShift = shifts.find((s: any) => s.shiftName?.trim().toLowerCase() === 'day shift');
            const nightShift = shifts.find((s: any) => s.shiftName?.trim().toLowerCase() === 'night shift');

            this.dayShiftOperators.set(dayShift?.operators?.map((op: any) => op.name) || []);
            this.nightShiftOperators.set(nightShift?.operators?.map((op: any) => op.name) || []);

            const totalHours = shifts.reduce((sum: number, s: any) => sum + (Number(s.actualWorkingHours) || 0), 0);
            const totalOut = shifts.reduce((sum: number, s: any) => sum + (Number(s.actualOutput) || 0), 0);
            this.outputAveragePerHour.set(totalHours > 0 ? Math.round(totalOut / totalHours) : 0);
          }
        });

        const deptId = this.departmentId();
        if (deptId) {
          untracked(() => {
            this.showAllDayOps.set(false);
            this.showAllNightOps.set(false);
          });
        }


        this.monthlySub = this.machineService.getMonthlyReport(machineId, {
          year: Number(year),
          month: Number(month)
        }).subscribe((res: any) => {
          if (res?.data) {
            this.monthlyTotalAchieved.set(Number(res.data.totalAchievedImpressions) || 0);
            this.monthlyTotalMakeReady.set(Number(res.data.totalMakeReadyCount) || 0);
            this.monthlyTotalPlanned.set(Number(res.data.totalPlannedImpressions) || 0);
            this.monthlyTotalWorkedHours.set(Number(res.data.totalMachineWorkedHours) || 0);

            if (res.data.operatorsMetrics && Array.isArray(res.data.operatorsMetrics)) {
              const mappedOps = res.data.operatorsMetrics.map((op: any) => {
                const totalOut = Number(op.monthlyOutput) || 0;
                const hours = Number(op.actualWorkingHours) || 0;
                return {
                  ...op,
                  operatorName: op.operatorName,
                  machineTotalOutput: totalOut,
                  machineAvgHourlyOutput: hours > 0 ? Math.round(totalOut / hours) : 0,
                  totalHoursWorked: hours
                };
              });

              const sorted = mappedOps.sort((a: any, b: any) => b.machineTotalOutput - a.machineTotalOutput);
              const topOutput = sorted.length > 0 ? sorted[0].machineTotalOutput : 0;
              const finalized = sorted.map((op: any, index: number) => ({
                ...op,
                rank: index + 1,
                gapToTop: topOutput - op.machineTotalOutput
              }));
              this.operatorPerformances.set(finalized);
            } else {
              this.operatorPerformances.set([]);
            }
          }
        });

        // Operator performance mapped from monthlySub
      }
    }, { allowSignalWrites: true });

    // ── Machine 2 data effects ────────────────────────────────────────────────
    effect(() => {
      this.refreshTrigger();
      const machineId = this.selectedMachineId2();
      const date = this.selectedDate();

      this.impressionsSub2?.unsubscribe();
      this.monthlySub2?.unsubscribe();

      if (machineId && date) {
        const [year, month, day] = date.split('-');

        this.impressionsSub2 = this.machineService.getDailyReport(machineId, { year, month, day }).subscribe((res: any) => {
          if (res && res.data) {
            this.dailyPlanned2.set(Number(res.data.plannedImpressions) || 0);
            this.dailyOutput2.set(Number(res.data.actualImpressions) || 0);
            this.makeReadyCount2.set(Number(res.data.makeReadyCount) || 0);

            const shifts = res.data.shiftWiseDetails || [];
            this.shiftDetails2.set(shifts);
            this.unitOfMachine2.set(res.data.unitOfMachine || 'impressions');
            if (res.data.currentShiftDate === date) {
              this.activeShiftName2.set(res.data.currentShift?.trim() || null);
            } else {
              this.activeShiftName2.set(null);
            }

            const dayShift = shifts.find((s: any) => s.shiftName?.trim().toLowerCase() === 'day shift');
            const nightShift = shifts.find((s: any) => s.shiftName?.trim().toLowerCase() === 'night shift');

            this.dayShiftOperators2.set(dayShift?.operators?.map((op: any) => op.name) || []);
            this.nightShiftOperators2.set(nightShift?.operators?.map((op: any) => op.name) || []);

            const totalHours = shifts.reduce((sum: number, s: any) => sum + (Number(s.actualWorkingHours) || 0), 0);
            const totalOut = shifts.reduce((sum: number, s: any) => sum + (Number(s.actualOutput) || 0), 0);
            this.outputAveragePerHour2.set(totalHours > 0 ? Math.round(totalOut / totalHours) : 0);
          }
        });

        this.monthlySub2 = this.machineService.getMonthlyReport(machineId, {
          year: Number(year),
          month: Number(month)
        }).subscribe((res: any) => {
          if (res?.data) {
            this.monthlyTotalAchieved2.set(Number(res.data.totalAchievedImpressions) || 0);
            this.monthlyTotalMakeReady2.set(Number(res.data.totalMakeReadyCount) || 0);
            this.monthlyTotalPlanned2.set(Number(res.data.totalPlannedImpressions) || 0);
            this.monthlyTotalWorkedHours2.set(Number(res.data.totalMachineWorkedHours) || 0);

            if (res.data.operatorsMetrics && Array.isArray(res.data.operatorsMetrics)) {
              const mappedOps = res.data.operatorsMetrics.map((op: any) => {
                const totalOut = Number(op.monthlyOutput) || 0;
                const hours = Number(op.actualWorkingHours) || 0;
                return {
                  ...op,
                  operatorName: op.operatorName,
                  machineTotalOutput: totalOut,
                  machineAvgHourlyOutput: hours > 0 ? Math.round(totalOut / hours) : 0,
                  totalHoursWorked: hours
                };
              });

              const sorted = mappedOps.sort((a: any, b: any) => b.machineTotalOutput - a.machineTotalOutput);
              const topOutput = sorted.length > 0 ? sorted[0].machineTotalOutput : 0;
              const finalized = sorted.map((op: any, index: number) => ({
                ...op,
                rank: index + 1,
                gapToTop: topOutput - op.machineTotalOutput
              }));
              this.operatorPerformances2.set(finalized);
            } else {
              this.operatorPerformances2.set([]);
            }
          }
        });

        const deptId = this.departmentId();
        if (deptId) {
          untracked(() => {
            this.showAllDayOps2.set(false);
            this.showAllNightOps2.set(false);
          });
        }

        // Operator performance mapped from monthlySub2
      }
    }, { allowSignalWrites: true });

    // ── Shared Active Shifts ──────────────────────────────────────────────────
    effect(() => {
      this.refreshTrigger();
      const deptId = this.departmentId();
      if (!deptId) return;

      this.activeShiftSub?.unsubscribe();
      this.activeShiftSub = this.machineService.getMachineCurrentShifts(deptId).subscribe((res: any) => {
        this.sharedCurrentShifts.set(res?.data?.machines || []);
      });
    }, { allowSignalWrites: true });

    // ── Realtime Shift Tracking (Machine 1) ──────────────────────────────────
    effect(() => {
      const allShifts = this.sharedCurrentShifts();
      const machineId = this.selectedMachineId();
      const date = this.selectedDate();
      if (!machineId) return;

      const shift = allShifts.find((m: any) => m.machineId === machineId);

      untracked(() => {
        this.realtimeShiftName.set(shift?.currentShift || null);

        if (shift && shift.shiftDate) {
          this.liveShiftDate.set(shift.shiftDate);
          if (this.isTrackingLiveDate && this.selectedDate() !== shift.shiftDate) {
            this.selectedDate.set(shift.shiftDate);
            this.isTrackingLiveDate = false;
          }
        } else {
          this.liveShiftDate.set(null);
        }

        if (shift && shift.shiftDate === date) {
          this.activeShiftName.set(shift.currentShift || null);
        } else {
          this.activeShiftName.set(null);
        }

        this.machineStatus.set(shift?.machineStatus?.toLowerCase() || 'unknown');
      });
    }, { allowSignalWrites: true });

    // ── Realtime Shift Tracking (Machine 2) ──────────────────────────────────
    effect(() => {
      const allShifts = this.sharedCurrentShifts();
      const machineId = this.selectedMachineId2();
      const date = this.selectedDate();
      if (!machineId) return;

      const shift = allShifts.find((m: any) => m.machineId === machineId);

      untracked(() => {
        this.realtimeShiftName2.set(shift?.currentShift || null);

        if (shift && shift.shiftDate) {
          this.liveShiftDate2.set(shift.shiftDate);
        } else {
          this.liveShiftDate2.set(null);
        }

        if (shift && shift.shiftDate === date) {
          this.activeShiftName2.set(shift.currentShift || null);
        } else {
          this.activeShiftName2.set(null);
        }

        this.machineStatus2.set(shift?.machineStatus?.toLowerCase() || 'unknown');
      });
    }, { allowSignalWrites: true });

    // â”€â”€ Fetch NCR for Current Operators â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    effect(() => {
      this.refreshTrigger();
      const ops1 = this.operatorPerformances();
      const ops2 = this.operatorPerformances2();
      const date = this.selectedDate();

      if (!date) return;
      const [year, month] = date.split('-');

      const startDate = `${year}-${month}-01`;
      const endDate = new Date(Number(year), Number(month), 0).toISOString().split('T')[0];

      const empNos = new Set<string>();
      ops1.forEach((o: any) => { if (o.empNo) empNos.add(o.empNo); });
      ops2.forEach((o: any) => { if (o.empNo) empNos.add(o.empNo); });

      empNos.forEach(empNo => {
        untracked(() => {
          this.operatorService.getNcr({
            employeeNo: empNo,
            startDate,
            endDate,
            status: 'approved',
            itemsPerPage: -1
          }, false).subscribe((res: any) => {
            const current = this.operatorNcrCounts();
            const currentData = this.operatorNcrData();
            const currentTotals = this.operatorNcrTotals();

            const dataArray = res?.data?.data || res?.data || [];
            let safeDataArray = Array.isArray(dataArray) ? dataArray : [];
            
            // Filter only approved records
            safeDataArray = safeDataArray.filter((ncr: any) => !ncr.status || ncr.status.toLowerCase() === 'approved');
            
            const count = safeDataArray.length;
            const totalVal = safeDataArray.reduce((sum: number, ncr: any) => sum + (Number(ncr.ncrValue) || 0), 0);

            this.operatorNcrCounts.set({
              ...current,
              [empNo]: count
            });
            this.operatorNcrData.set({
              ...currentData,
              [empNo]: safeDataArray
            });
            this.operatorNcrTotals.set({
              ...currentTotals,
              [empNo]: totalVal
            });
          });
        });
      });
    }, { allowSignalWrites: true });

  }

  protected getOperatorIrCount(empNo: string | undefined): number {
    if (!empNo) return 0;
    const ncrList = this.operatorNcrData()[empNo] || [];
    return ncrList.filter((ncr: any) => ncr.ncrType?.toUpperCase() === 'IRCC').length;
  }

  protected getOperatorCcCount(empNo: string | undefined): number {
    if (!empNo) return 0;
    const ncrList = this.operatorNcrData()[empNo] || [];
    return ncrList.filter((ncr: any) => ncr.ncrType?.toUpperCase() === 'CC').length;
  }

  private impressionsSub?: Subscription;
  private monthlySub?: Subscription;
  private rosterSub?: Subscription;
  private todayRosterSub?: Subscription;
  private activeShiftSub?: Subscription;
  private chartDataSub?: Subscription;

  // Machine 2 subscriptions
  private impressionsSub2?: Subscription;
  private monthlySub2?: Subscription;
  private chartDataSub2?: Subscription;

  private extractTimeStr(str: string | null | undefined): string {
    if (!str) return '08:00';
    const match = String(str).match(/(\d{1,2})[:.:](\d{2})/);
    if (match) return `${match[1].padStart(2, '0')}:${match[2]}`;
    return '08:00';
  }

  private parseTimeToTimestamp(timeStr: string | null | undefined, windowStartHour: number, baseDate: Date): number {
    if (!timeStr) return NaN;

    let h = 8;
    let m = 0;

    const match = String(timeStr).match(/(\d{1,2})[:.:](\d{2})/);
    if (match) {
      h = parseInt(match[1], 10);
      m = parseInt(match[2], 10);
    } else {
      const fallbackMatch = String(timeStr).match(/(\d{1,2})/);
      if (fallbackMatch) {
        h = parseInt(fallbackMatch[1], 10);
      }
    }

    const dt = new Date(baseDate);
    dt.setHours(h, m, 0, 0);
    if (h < windowStartHour) {
      dt.setDate(dt.getDate() + 1);
    }
    return dt.getTime();
  }

  private buildTimeline(payload: any, dateStr: string) {
    if (!payload) {
      this.timelineSegments.set([]);
      this.timelineAxis.set([]);
      this.timelineTotalDurationStr.set('0h 0m');
      this.timelineWindow.set(null);
      this.makeReadyTimerStr.set('00:00:00');
      this.makeReadyTimerStatus.set('idle');
      this.makeReadyBaseMs1 = 0;
      this.makeReadyRunningStartTs1 = undefined;
      return;
    }

    const mrResult = this.processMakeReadyTimerData(payload, dateStr);
    this.makeReadyTimerStr.set(mrResult.timerStr);
    this.makeReadyTimerStatus.set(mrResult.status);
    this.makeReadyBaseMs1 = mrResult.baseMs;
    this.makeReadyRunningStartTs1 = mrResult.runningStartTs;

    const opResult = this.processOperationalTimerData(payload, dateStr);
    this.operationalTimerStr.set(opResult.timerStr);
    this.operationalTimerStatus.set(opResult.status);
    this.operationalBaseMs1 = opResult.baseMs;
    this.operationalRunningStartTs1 = opResult.runningStartTs;

    const data = Array.isArray(payload) ? payload[0] : payload;
    if (!data) return;

    let startTs: number;
    let endTs: number;

    const [y, m, d] = dateStr.split('-');
    const defaultBaseDate = new Date(Number(y), Number(m) - 1, Number(d));

    if (data.windowStart && String(data.windowStart).includes('-')) {
      startTs = new Date(String(data.windowStart).replace(' ', 'T')).getTime();
      endTs = new Date(String(data.windowEnd).replace(' ', 'T')).getTime();
    } else {
      const wStart = this.extractTimeStr(data.windowStart);
      const wEnd = this.extractTimeStr(data.windowEnd);
      const stH = parseInt(wStart.split(':')[0], 10);
      startTs = this.parseTimeToTimestamp(wStart, stH, defaultBaseDate);
      endTs = this.parseTimeToTimestamp(wEnd, stH, defaultBaseDate);
      if (endTs <= startTs) endTs += 24 * 60 * 60 * 1000;
    }

    if (isNaN(startTs) || isNaN(endTs)) return;
    const totalMs = endTs - startTs;
    if (totalMs <= 0 || isNaN(totalMs)) return;

    const windowStartStr = this.extractTimeStr(data.windowStart);
    const startH = parseInt(windowStartStr.split(':')[0], 10);

    const baseDate = new Date(startTs);
    baseDate.setHours(0, 0, 0, 0);

    const startDt = new Date(startTs);
    const endDt = new Date(endTs);

    const formatDt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const HH = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${HH}:${min}`;
    };

    this.timelineWindow.set({
      start: formatDt(startDt),
      end: formatDt(endDt)
    });

    const totalMins = Math.round(totalMs / 60000);
    const totalHrsStr = Math.floor(totalMins / 60);
    const remMins = totalMins % 60;
    this.timelineTotalDurationStr.set(`${totalHrsStr}h ${remMins}m`);

    const segments: any[] = [];
    const nowTs = Date.now();

    const mapRanges = (ranges: any[], type: string, colorClass: string) => {
      if (!Array.isArray(ranges)) return;
      ranges.forEach((r: any) => {
        let segStartTs = this.parseTimeToTimestamp(r.x, startH, baseDate);
        let segEndTs = this.parseTimeToTimestamp(r.x2, startH, baseDate);

        if (isNaN(segStartTs) || isNaN(segEndTs)) return;
        if (segEndTs < segStartTs) segEndTs += 24 * 60 * 60 * 1000;

        segStartTs = Math.max(startTs, Math.min(segStartTs, endTs));
        segEndTs = Math.max(startTs, Math.min(segEndTs, endTs));
        segStartTs = Math.min(segStartTs, nowTs);
        segEndTs = Math.min(segEndTs, nowTs);

        if (segEndTs > segStartTs) {
          const leftPct = ((segStartTs - startTs) / totalMs) * 100;
          const widthPct = ((segEndTs - segStartTs) / totalMs) * 100;
          const durMins = Math.round((segEndTs - segStartTs) / 60000);
          const durH = Math.floor(durMins / 60);
          const durM = durMins % 60;
          const durStr = durH > 0 ? `${durH}h ${durM}m` : `${durM}m`;

          segments.push({
            type,
            class: colorClass,
            left: leftPct,
            width: widthPct,
            label: '',
            tooltip: `${type} (${r.x} - ${r.x2}) - ${durStr}`
          });
        }
      });
    };

    mapRanges(data.operationalRanges, 'Operational', 'mc-seg-run');
    mapRanges(data.idleRanges, 'Idle', 'mc-seg-idle');
    mapRanges(data.maintenanceRanges, 'Maintenance', 'mc-seg-maintenance');
    mapRanges(data.holdRanges, 'Hold', 'mc-seg-hold');
    mapRanges(data.makeReadyRanges, 'Make Ready', 'mc-seg-mr');
    mapRanges(data.heatCoolRanges, 'Heat/Cool', 'mc-seg-heatcool');

    this.timelineSegments.set(segments);

    const axis: string[] = [];
    const stepMs = totalMs / 6;
    for (let i = 0; i <= 6; i++) {
      const d = new Date(startTs + (i * stepMs));
      axis.push(`${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`);
    }
    this.timelineAxis.set(axis);
  }

  /** Timeline builder for Machine 2 */
  private buildTimeline2(payload: any, dateStr: string) {
    if (!payload) {
      this.timelineSegments2.set([]);
      this.timelineAxis2.set([]);
      this.timelineTotalDurationStr2.set('0h 0m');
      this.timelineWindow2.set(null);
      this.makeReadyTimerStr2.set('00:00:00');
      this.makeReadyTimerStatus2.set('idle');
      this.makeReadyBaseMs2 = 0;
      this.makeReadyRunningStartTs2 = undefined;
      return;
    }

    const mrResult2 = this.processMakeReadyTimerData(payload, dateStr);
    this.makeReadyTimerStr2.set(mrResult2.timerStr);
    this.makeReadyTimerStatus2.set(mrResult2.status);
    this.makeReadyBaseMs2 = mrResult2.baseMs;
    this.makeReadyRunningStartTs2 = mrResult2.runningStartTs;

    const opResult2 = this.processOperationalTimerData(payload, dateStr);
    this.operationalTimerStr2.set(opResult2.timerStr);
    this.operationalTimerStatus2.set(opResult2.status);
    this.operationalBaseMs2 = opResult2.baseMs;
    this.operationalRunningStartTs2 = opResult2.runningStartTs;

    const data = Array.isArray(payload) ? payload[0] : payload;
    if (!data) return;

    let startTs: number;
    let endTs: number;

    const [y, m, d] = dateStr.split('-');
    const defaultBaseDate = new Date(Number(y), Number(m) - 1, Number(d));

    if (data.windowStart && String(data.windowStart).includes('-')) {
      startTs = new Date(String(data.windowStart).replace(' ', 'T')).getTime();
      endTs = new Date(String(data.windowEnd).replace(' ', 'T')).getTime();
    } else {
      const wStart = this.extractTimeStr(data.windowStart);
      const wEnd = this.extractTimeStr(data.windowEnd);
      const stH = parseInt(wStart.split(':')[0], 10);
      startTs = this.parseTimeToTimestamp(wStart, stH, defaultBaseDate);
      endTs = this.parseTimeToTimestamp(wEnd, stH, defaultBaseDate);
      if (endTs <= startTs) endTs += 24 * 60 * 60 * 1000;
    }

    if (isNaN(startTs) || isNaN(endTs)) return;
    const totalMs = endTs - startTs;
    if (totalMs <= 0 || isNaN(totalMs)) return;

    const windowStartStr = this.extractTimeStr(data.windowStart);
    const startH = parseInt(windowStartStr.split(':')[0], 10);

    const baseDate = new Date(startTs);
    baseDate.setHours(0, 0, 0, 0);

    const startDt = new Date(startTs);
    const endDt = new Date(endTs);

    const formatDt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const HH = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${HH}:${min}`;
    };

    this.timelineWindow2.set({
      start: formatDt(startDt),
      end: formatDt(endDt)
    });

    const totalMins = Math.round(totalMs / 60000);
    const totalHrsStr = Math.floor(totalMins / 60);
    const remMins = totalMins % 60;
    this.timelineTotalDurationStr2.set(`${totalHrsStr}h ${remMins}m`);

    const segments: any[] = [];
    const nowTs = Date.now();

    const mapRanges = (ranges: any[], type: string, colorClass: string) => {
      if (!Array.isArray(ranges)) return;
      ranges.forEach((r: any) => {
        let segStartTs = this.parseTimeToTimestamp(r.x, startH, baseDate);
        let segEndTs = this.parseTimeToTimestamp(r.x2, startH, baseDate);

        if (isNaN(segStartTs) || isNaN(segEndTs)) return;
        if (segEndTs < segStartTs) segEndTs += 24 * 60 * 60 * 1000;

        segStartTs = Math.max(startTs, Math.min(segStartTs, endTs));
        segEndTs = Math.max(startTs, Math.min(segEndTs, endTs));
        segStartTs = Math.min(segStartTs, nowTs);
        segEndTs = Math.min(segEndTs, nowTs);

        if (segEndTs > segStartTs) {
          const leftPct = ((segStartTs - startTs) / totalMs) * 100;
          const widthPct = ((segEndTs - segStartTs) / totalMs) * 100;
          const durMins = Math.round((segEndTs - segStartTs) / 60000);
          const durH = Math.floor(durMins / 60);
          const durM = durMins % 60;
          const durStr = durH > 0 ? `${durH}h ${durM}m` : `${durM}m`;

          segments.push({
            type,
            class: colorClass,
            left: leftPct,
            width: widthPct,
            label: '',
            tooltip: `${type} (${r.x} - ${r.x2}) - ${durStr}`
          });
        }
      });
    };

    mapRanges(data.operationalRanges, 'Operational', 'mc-seg-run');
    mapRanges(data.idleRanges, 'Idle', 'mc-seg-idle');
    mapRanges(data.maintenanceRanges, 'Maintenance', 'mc-seg-maintenance');
    mapRanges(data.holdRanges, 'Hold', 'mc-seg-hold');
    mapRanges(data.makeReadyRanges, 'Make Ready', 'mc-seg-mr');
    mapRanges(data.heatCoolRanges, 'Heat/Cool', 'mc-seg-heatcool');

    this.timelineSegments2.set(segments);

    const axis: string[] = [];
    const stepMs = totalMs / 6;
    for (let i = 0; i <= 6; i++) {
      const d = new Date(startTs + (i * stepMs));
      axis.push(`${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`);
    }
    this.timelineAxis2.set(axis);
  }

  private processTimerData(
    payload: any, 
    dateStr: string, 
    primaryType: 'make_ready' | 'operational', 
    resetType: 'operational' | 'make_ready'
  ): {
    timerStr: string,
    status: 'running' | 'paused' | 'reset' | 'idle',
    baseMs: number,
    runningStartTs?: number
  } {
    if (!payload) return { timerStr: '00:00:00', status: 'idle', baseMs: 0 };
    const data = Array.isArray(payload) ? payload[0] : payload;
    if (!data) return { timerStr: '00:00:00', status: 'idle', baseMs: 0 };

    let windowStartTs: number;
    const [y, m, d] = dateStr.split('-');
    const defaultBaseDate = new Date(Number(y), Number(m) - 1, Number(d));

    if (data.windowStart && String(data.windowStart).includes('-')) {
      windowStartTs = new Date(String(data.windowStart).replace(' ', 'T')).getTime();
    } else {
      const wStart = this.extractTimeStr(data.windowStart);
      const stH = parseInt(wStart.split(':')[0], 10);
      windowStartTs = this.parseTimeToTimestamp(wStart, stH, defaultBaseDate);
    }

    if (isNaN(windowStartTs)) return { timerStr: '00:00:00', status: 'idle', baseMs: 0 };

    const windowStartHour = new Date(windowStartTs).getHours();
    const baseDate = new Date(windowStartTs);
    baseDate.setHours(0, 0, 0, 0);

    interface EventItem {
      type: 'make_ready' | 'hold' | 'operational';
      jobOperationId: number;
      startTs: number;
      endTs: number;
      ongoing: boolean;
    }
    const events: EventItem[] = [];

    const extractEvents = (ranges: any[], type: 'make_ready' | 'hold' | 'operational') => {
      if (!Array.isArray(ranges)) return;
      ranges.forEach((r: any) => {
        if (r.jobOperationId != null) {
          let startTs = this.parseTimeToTimestamp(r.x, windowStartHour, baseDate);
          let endTs = this.parseTimeToTimestamp(r.x2, windowStartHour, baseDate);
          if (!isNaN(startTs) && !isNaN(endTs)) {
            if (endTs < startTs) endTs += 24 * 60 * 60 * 1000;
            events.push({
              type,
              jobOperationId: Number(r.jobOperationId),
              startTs,
              endTs,
              ongoing: r.ongoing === 1 || r.ongoing === true
            });
          }
        }
      });
    };

    extractEvents(data.makeReadyRanges, 'make_ready');
    extractEvents(data.holdRanges, 'hold');
    extractEvents(data.operationalRanges, 'operational');

    if (events.length === 0) return { timerStr: '00:00:00', status: 'idle', baseMs: 0 };

    const nowTs = Date.now();
    let latestJobOpId = -1;
    let maxTs = -1;
    
    for (const ev of events) {
      const ts = ev.ongoing ? nowTs : ev.endTs;
      if (ts > maxTs) {
        maxTs = ts;
        latestJobOpId = ev.jobOperationId;
      }
    }

    if (latestJobOpId === -1) return { timerStr: '00:00:00', status: 'idle', baseMs: 0 };

    const jobEvents = events.filter(e => e.jobOperationId === latestJobOpId);

    let lastResetStartTs = 0;
    let isResetOngoing = false;
    for (const ev of jobEvents) {
      if (ev.type === resetType) {
        if (ev.startTs > lastResetStartTs) lastResetStartTs = ev.startTs;
        if (ev.ongoing || (nowTs >= ev.startTs && nowTs <= ev.endTs)) isResetOngoing = true;
      }
    }

    const primaryIntervals: {start: number, end: number}[] = [];
    const holdIntervals: {start: number, end: number}[] = [];
    
    let isPrimaryActiveNow = false;
    let isHoldActiveNow = false;

    for (const ev of jobEvents) {
      let s = ev.startTs;
      let e = ev.ongoing ? nowTs : ev.endTs;
      if (e <= lastResetStartTs) continue;
      if (s < lastResetStartTs) s = lastResetStartTs;
      
      const activeNow = ev.ongoing || (nowTs >= s && nowTs <= e);
      if (ev.type === primaryType) {
        primaryIntervals.push({start: s, end: e});
        if (activeNow) isPrimaryActiveNow = true;
      } else if (ev.type === 'hold') {
        holdIntervals.push({start: s, end: e});
        if (activeNow) isHoldActiveNow = true;
      }
    }

    const getUnion = (intervals: {start: number, end: number}[]) => {
      if (intervals.length === 0) return [];
      intervals.sort((a, b) => a.start - b.start);
      const res = [ { ...intervals[0] } ];
      for (let i = 1; i < intervals.length; i++) {
        const last = res[res.length - 1];
        if (intervals[i].start <= last.end) {
          last.end = Math.max(last.end, intervals[i].end);
        } else {
          res.push({ ...intervals[i] });
        }
      }
      return res;
    };

    const getIntersectionDur = (start: number, end: number, unionIntervals: {start: number, end: number}[]) => {
      let dur = 0;
      for (const inv of unionIntervals) {
        const s = Math.max(start, inv.start);
        const e = Math.min(end, inv.end);
        if (s < e) dur += (e - s);
      }
      return dur;
    };

    const unionPrimary = getUnion(primaryIntervals);
    const unionHold = getUnion(holdIntervals);

    let totalMs = 0;
    for (const p of unionPrimary) {
      const dur = p.end - p.start;
      const holdOverlap = getIntersectionDur(p.start, p.end, unionHold);
      totalMs += (dur - holdOverlap);
    }

    let status: 'running' | 'paused' | 'reset' | 'idle' = 'idle';
    let baseMs = totalMs;
    let runningStartTs: number | undefined = undefined;

    if (isResetOngoing) {
      status = 'reset';
      baseMs = 0;
    } else if (isPrimaryActiveNow && !isHoldActiveNow) {
      status = 'running';
      baseMs = totalMs;
      runningStartTs = nowTs;
    } else if (isPrimaryActiveNow && isHoldActiveNow) {
      status = 'paused';
    } else if (unionPrimary.length > 0) {
      status = 'paused';
    }

    return {
      timerStr: this.formatTimerMs(baseMs),
      status,
      baseMs,
      runningStartTs
    };
  }

  private processMakeReadyTimerData(payload: any, dateStr: string) {
    return this.processTimerData(payload, dateStr, 'make_ready', 'operational');
  }

  private processOperationalTimerData(payload: any, dateStr: string) {
    return this.processTimerData(payload, dateStr, 'operational', 'make_ready');
  }

  private formatTimerMs(ms: number): string {
    if (isNaN(ms) || ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  }

  protected isTimerExceeded(timerStr: string, limitMinutes: number = 45): boolean {
    if (!timerStr) return false;
    const parts = timerStr.split(':').map(Number);
    if (parts.length < 2) return false;
    let totalSeconds = 0;
    if (parts.length === 3) {
      totalSeconds = (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
    } else if (parts.length === 2) {
      totalSeconds = (parts[0] || 0) * 60 + (parts[1] || 0);
    }
    return totalSeconds >= limitMinutes * 60;
  }

  private tickTimers(): void {
    const now = Date.now();

    if (this.makeReadyTimerStatus() === 'running' && this.makeReadyRunningStartTs1) {
      const elapsed = Math.max(0, now - this.makeReadyRunningStartTs1);
      const totalMs = this.makeReadyBaseMs1 + elapsed;
      this.makeReadyTimerStr.set(this.formatTimerMs(totalMs));
    }

    if (this.makeReadyTimerStatus2() === 'running' && this.makeReadyRunningStartTs2) {
      const elapsed = Math.max(0, now - this.makeReadyRunningStartTs2);
      const totalMs = this.makeReadyBaseMs2 + elapsed;
      this.makeReadyTimerStr2.set(this.formatTimerMs(totalMs));
    }

    if (this.operationalTimerStatus() === 'running' && this.operationalRunningStartTs1) {
      const elapsed = Math.max(0, now - this.operationalRunningStartTs1);
      const totalMs = this.operationalBaseMs1 + elapsed;
      this.operationalTimerStr.set(this.formatTimerMs(totalMs));
    }

    if (this.operationalTimerStatus2() === 'running' && this.operationalRunningStartTs2) {
      const elapsed = Math.max(0, now - this.operationalRunningStartTs2);
      const totalMs = this.operationalBaseMs2 + elapsed;
      this.operationalTimerStr2.set(this.formatTimerMs(totalMs));
    }
  }

  ngOnInit(): void {
    const now = new Date();
    this.today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    this.currentTime = now.toLocaleTimeString();
    this.lastUpdatedTime.set(this.currentTime);

    this.clockSub = interval(1000).subscribe(() => {
      const currentNow = new Date();
      this.currentTime = currentNow.toLocaleTimeString();
      const todayStr = `${currentNow.getFullYear()}-${String(currentNow.getMonth() + 1).padStart(2, '0')}-${String(currentNow.getDate()).padStart(2, '0')}`;
      if (this.today !== todayStr) {
        this.today = todayStr;
      }
      this.tickTimers();
    });

    this.refreshSub = interval(5 * 60 * 1000).subscribe(() => {
      this.refreshTrigger.update(v => v + 1);
      this.lastUpdatedTime.set(new Date().toLocaleTimeString());
    });
  }

  ngOnDestroy(): void {
    this.clockSub?.unsubscribe();
    this.refreshSub?.unsubscribe();
  }

  // â”€â”€ Data fetching â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  private fetchMachineTypes(): void {
    this.machineTypeService.getAll().subscribe({
      next: result => {
        if (result.data?.length) this.machineTypes.set(result.data);
      }
    });
  }

  private fetchDepartments(): void {
    this.departmentService.getAllDepartments().subscribe({
      next: result => {
        if (result.data?.departments) this.$$departmentDTO.set(result.data.departments);
      }
    });
  }

  // â”€â”€ Event handlers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }

  selectTab(tab: MachineTypeDTO): void {
    this.activeTab.set(tab);
  }

  onDepartmentChange(departmentId: number): void {
    this.departmentId.set(departmentId);
  }

  onMachineChange(machineId: number): void {
    this.selectedMachineId.set(machineId);
  }

  // â”€â”€ NCR Modal Methods â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  openNcrModal(empNo: string, operatorName: string): void {
    if (!empNo) return;

    this.selectedNcrOperatorName.set(operatorName || empNo);
    this.operatorNcrDetails.set(this.operatorNcrData()[empNo] || []);
    this.ncrModalOpen.set(true);
  }

  closeNcrModal(): void {
    this.ncrModalOpen.set(false);
    this.selectedNcrOperatorName.set('');
    this.operatorNcrDetails.set([]);
  }
}
